import * as React from 'react';
import { isNil } from 'lodash';
import { connect } from 'react-redux';
import classnames from 'classnames';

import ActionLink from 'components/buttons/action-link/ActionLink';
import UserDropdownList from '../user-dropdown-list/UserDropdownList';

import { logout } from 'modules/access/reducers/session';
import { selectOrganization } from 'modules/organizations/reducers/organizations';
import { addUserAction } from 'modules/tracking/providers/user-action-provider';

import navigate from 'libs/router/navigate';

import { __t, applyTranslations } from 'utils/language';
import { redirectToIndexPage } from 'utils/routes';
import { getSortedOrganizationsWithEntities } from 'utils/view';
import { getDefaultProfileUrl } from 'utils/navigation';
import { filterAgencies } from 'utils/organization';
import {
  getBrandIdFromOrganization,
  getInfluencerIdFromOrganization,
  getOrganizationData
} from '../../../utils/metrics-data';

import './user-dropdown.scss';

import type { Account, UserDropdownOrganization } from 'types';

applyTranslations(require('./user-dropdown.i18n.json'));

type OwnProps = {
  children: React.ReactNode
};

type StateProps = {
  organizationsList: Array<UserDropdownOrganization>,
  organizationsMap: {
    [x: string]: UserDropdownOrganization
  },
  currentAccount: Account | undefined | null,
  currentOrganizationId: number | null
};

type DispatchProps = {
  dispatchLogout: () => Promise<void>,
  dispatchSelectOrganization: (a: UserDropdownOrganization) => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  show: boolean
};

class UserDropdown extends React.Component<Props, State> {
  state = {
    show: false
  };

  handleHide = () => {
    this.setState({
      show: false
    });
  };

  handleToggle = () => {
    const gaTrackingCallback = (): void => {
      const { currentOrganizationId } = this.props;

      if (currentOrganizationId === null) {
        return;
      }

      if (this.state.show === false) {
        return;
      }

      addUserAction({
        eventCategory: 'drop down',
        eventAction: 'open menu'
      });
    };

    this.setState(state => ({
      show: !state.show
    }), gaTrackingCallback);
  };

  handleLogout = () => {
    const { dispatchLogout } = this.props;

    this.handleHide();

    dispatchLogout()
      .then(() => redirectToIndexPage());
  };

  handleSelectOrganization = (orgId: string, href?: string | null): void => {
    const { organizationsMap, dispatchSelectOrganization } = this.props;
    const organization = organizationsMap[orgId];

    if (isNil(organization)) {
      return;
    }

    dispatchSelectOrganization(organization);

    if (!href) {
      navigate.to(getDefaultProfileUrl(organization));
    }

    this.handleHide();

    const entityType = organization.type.indexOf('brand') > -1 ? 'brand' : 'creator';
    const entityId = entityType === 'brand'
      ? getBrandIdFromOrganization(organization)
      : getInfluencerIdFromOrganization(organization);

    addUserAction({
      eventCategory: 'drop down',
      eventAction: 'navigate',
      stage: 'start',
      destination: entityType,
      customDimensionData: getOrganizationData(organization),
      eventLabel: {
        type: entityType,
        id: entityId ?? -1
      }
    });
  };

  render() {
    const { children, organizationsList, currentAccount } = this.props;

    const classes = classnames({
      'user-dropdown': true,
      'user-dropdown--visible': this.state.show
    });

    const sortedOrganizations = getSortedOrganizationsWithEntities(organizationsList);
    const organizations = filterAgencies(sortedOrganizations);

    return (
      <section
        className="user-dropdown-wrapper"
        data-qa="user-dropdown-wrapper"
      >
        <div
          className="user-dropdown-wrapper__action"
          onClick={this.handleToggle}
        >
          {children}
        </div>
        {
          this.state.show && (
            <div className="user-dropdown-wrapper__overlay" onClick={this.handleHide} />
          )
        }
        {
          currentAccount && (
            <div className={classes}>
              <UserDropdownList
                organizations={organizations}
                isOpen={this.state.show}
                onSelect={this.handleSelectOrganization}
                onToggle={this.handleToggle}
              />
              <div className="user-dropdown__footer">
                <ActionLink
                  onClick={this.handleLogout}
                  data={{ qa: 'user-dropdown__item-logout' }}
                >
                  {__t('user_dropdown.logout')}
                </ActionLink>
              </div>
            </div>
          )
        }
      </section>
    );
  }
}

const mapStateToProps = (state): StateProps => ({
  currentAccount: state.account.currentAccount,
  organizationsList: state.organizations.list,
  organizationsMap: state.organizations.mapByOrganizationId,
  currentOrganizationId: state.organizations.currentOrganizationId
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchSelectOrganization: (organization) => dispatch(selectOrganization(organization)),
  dispatchLogout: () => dispatch(logout())
});

export default connect(mapStateToProps, mapDispatchToProps)(UserDropdown);
